import { useCallback, useMemo } from "react"

import { useSessionStorage } from "./useSessionStorage"

/**
 * Hook to track which simulation outputs have been investigated for a given run.
 * Stores the investigated IDs in sessionStorage, keyed by run_id.
 * Data persists only for the current browser session (cleared when tab/window closes).
 */
export const useInvestigatedSimulationOutputs = (runId: string | undefined) => {
  // Store as array in sessionStorage, convert to Set for use
  const [investigatedIdsArray, setInvestigatedIdsArray] = useSessionStorage<
    string[]
  >(`investigated-simulation-outputs-${runId || ""}`, [])

  const investigatedIds = useMemo(
    () => new Set(investigatedIdsArray),
    [investigatedIdsArray],
  )

  const markAsInvestigated = useCallback(
    (simulationOutputId: string) => {
      if (!simulationOutputId) {
        return
      }
      setInvestigatedIdsArray((prev: string[]) => {
        if (prev.includes(simulationOutputId)) {
          return prev
        }
        return [...prev, simulationOutputId]
      })
    },
    [setInvestigatedIdsArray],
  )

  const isInvestigated = useCallback(
    (simulationOutputId: string): boolean => {
      if (!simulationOutputId) {
        return false
      }
      return investigatedIds.has(simulationOutputId)
    },
    [investigatedIds],
  )

  const clearInvestigated = useCallback(() => {
    setInvestigatedIdsArray([])
  }, [setInvestigatedIdsArray])

  return {
    investigatedIds,
    markAsInvestigated,
    isInvestigated,
    clearInvestigated,
  }
}
